import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import './BrandSearch.scss'
import _ from 'lodash'
import { Button, Message, Popup, Search, SearchResultData } from 'semantic-ui-react'
import { Brand } from '../../redux/types/products'
import { clearNewBrand, createBrand } from '../../redux/actions/products'
import { AppState } from '../../redux/store'

const initialState = {
  loading: false,
  results: [],
  value: '',
  resultSelected: false,
}

function brandSearchReducer(state: any, action: any) {
  switch (action.type) {
    case 'CLEAN_QUERY':
      return initialState
    case 'START_SEARCH':
      return {
        ...state,
        loading: true,
        value: action.query,
        resultSelected: false,
      }
    case 'FINISH_SEARCH':
      return {
        ...state,
        loading: false,
        results: action.results,
        resultSelected: false,
      }
    case 'UPDATE_SELECTION':
      return {
        ...state,
        value: action.selection,
        loading: false,
        resultSelected: true,
      }
    default:
      throw new Error()
  }
}

interface OnSelect {
  (brand: Brand | null): any
}

interface BrandSearchProps {
  as: any
  currentBrandId?: string | number
  initialBrand?: string
  hideCreate?: boolean
  hideSelected?: boolean
  name: string
  noResultsMessage?: any
  noSelectionMessage?: string
  onQueryChange?: any // callback anytime the input changes value
  onSelect: OnSelect
  placeholder?: string
  source: Brand[]
}

export const BrandSearchAutoMatch = ({
  as,
  currentBrandId,
  hideCreate,
  hideSelected,
  initialBrand,
  name,
  noResultsMessage,
  noSelectionMessage,
  onQueryChange,
  onSelect,
  placeholder,
  source,
}: BrandSearchProps) => {
  const dispatch = useDispatch()
  const [hasTyped, setHasTyped] = useState(false)
  const newBrand = useSelector((state: AppState) => state.products.newBrand)
  const [brandSearchState, brandSearchDispatch] = React.useReducer(brandSearchReducer, initialState)

  const { loading, results, value } = brandSearchState

  const timeoutRef = React.useRef()
  const handleSearchChange = React.useCallback(
    (e, data) => {
      setHasTyped(true)
      clearTimeout(timeoutRef.current)
      brandSearchDispatch({ type: 'START_SEARCH', query: data.value })
      onQueryChange && onQueryChange(data.value)

      // @ts-ignore
      timeoutRef.current = setTimeout(() => {
        if (data.value.length === 0) {
          brandSearchDispatch({ type: 'CLEAN_QUERY' })
          return
        }

        const re = new RegExp(_.escapeRegExp(data.value), 'i')
        // https://stackoverflow.com/questions/990904/remove-accents-diacritics-in-a-string-in-javascript/37511463#37511463
        const isMatch = (result: Brand) =>
          re.test(result.name.normalize('NFD').replace(/[\u0300-\u036f]/g, ''))

        brandSearchDispatch({
          type: 'FINISH_SEARCH',
          results: _.filter(source, isMatch).map((result: Brand) => ({
            ...result,
            title: result.name,
          })),
        })
      }, 300)
    },
    [onQueryChange, source]
  )

  React.useEffect(() => {
    return () => {
      clearTimeout(timeoutRef.current)
    }
  }, [])

  // update the selection when newBrand is set
  // newBrand is set in the reducers after a new brand is created
  useEffect(() => {
    if (newBrand) {
      brandSearchDispatch({
        type: 'UPDATE_SELECTION',
        selection: newBrand.name,
      })
      onSelect(newBrand)
    }

    return () => {
      dispatch(clearNewBrand())
    }
  }, [dispatch, newBrand, onSelect])

  const createBrandBtn = function (currentInput: string, loading: boolean) {
    return (
      !hideCreate &&
      hasTyped && // don't show btn unless user has typed
      !brandSearchState.resultSelected &&
      value && ( // only show when there are no results // there must be a value in the input
        <div style={{ marginLeft: '5px' }}>
          <Button
            size="tiny"
            primary
            loading={loading}
            onClick={(_e) => {
              _e.preventDefault()
              dispatch(createBrand({ name: currentInput }))
            }}
          >
            Create Brand Named "{currentInput}"
          </Button>
        </div>
      )
    )
  }

  const currentBrand = useMemo(
    () =>
      source.find(
        (b: Brand) =>
          currentBrandId &&
          b.id === (typeof currentBrandId === 'number' ? currentBrandId : parseInt(currentBrandId))
      ),
    [currentBrandId, source]
  )

  // ran anytime a search result is clicked
  const onSelection = useCallback(
    (data: SearchResultData) => {
      brandSearchDispatch({
        type: 'UPDATE_SELECTION',
        selection: data.result.name,
      })
      onSelect(data.result)
    },
    [onSelect]
  )

  // this is used for currentBrandString exclusively
  // it was developed for the batch product creator
  // so, when a currentBrandString is supplied in the batch product creator
  // it will automatically match and select a brand
  useEffect(() => {
    if (!currentBrandId && source?.length > 0 && initialBrand && initialBrand?.length > 0) {
      const brand = source.find(
        (brand) => brand.name.toLowerCase().trim() === initialBrand.toLowerCase().trim()
      )
      if (brand) {
        onSelection({
          result: brand,
        })
      }
    }
  }, [source, onSelection, initialBrand, currentBrandId])

  return (
    <div className="brand-search-component">
      <div className="label-and-msg">
        <div className="brand-search-label-ctr">
          <span>Brand</span>
        </div>

        {!hideSelected && (
          <>
            <Message color="grey" className="no-brand-msg msg" hidden={!!currentBrandId}>
              {noSelectionMessage
                ? noSelectionMessage
                : 'This product does not have a brand assigned.'}
            </Message>
            <Message color="blue" className="selected-brand-msg msg" hidden={!currentBrandId}>
              <span>
                Selected: &nbsp;
                <Popup
                  content={`Product Brand ID: ${currentBrand?.id}`}
                  trigger={<strong style={{ marginRight: '10px' }}>{currentBrand?.name}</strong>}
                />
              </span>
            </Message>
          </>
        )}
      </div>
      <div className="search-bar-ctr">
        <Search
          id="brand-search-input"
          as={as}
          className="semantic-search-component"
          fluid={false}
          icon="search"
          placeholder={placeholder ?? 'Search brands'}
          name={name}
          loading={loading}
          minCharacters={2}
          noResultsMessage={noResultsMessage || 'No results found..'}
          selectFirstResult={true}
          onResultSelect={(e, data) => {
            onSelection(data)
          }}
          onSelectionChange={(e, data) => {
            onSelection(data)
          }}
          onSearchChange={handleSearchChange}
          results={results}
          value={value}
        />
        {createBrandBtn(value, loading)}
      </div>
    </div>
  )
}
